<template>
  <div class="container box">
    <img
      src="https://s3.us-east-2.amazonaws.com/ganttogether/custom-uploads%2F1620753365744-gant-travel.png"
      alt="Gant Travel"
      width="200"
    />

    <h1 class="is-size-3 mb-3">Travel Approval Request</h1>

    <div v-if="isLoading" class="notification is-light has-text-centered">
      <div class="is-size-4 has-text-grey">Loading..</div>
    </div>

    <div v-if="!isLoading && !isFound" class="notification is-danger is-light">
      Sorry, we could not find a travel approval request for that record locator
      and key. Please ensure both values are correct and try again.
    </div>

    <div v-if="!isLoading && isFound">
      <!--{{ travelRequestNotification.type }} to {{ travelRequestNotification.destination }}-->

      <div v-if="allSegmentsCancelled" class="notification is-info is-light">
        This trip was cancelled.
      </div>

      <div v-if="approvedAt" class="notification is-success is-light">
        This trip was approved on {{ approvedAt | formatDateTime }}
      </div>

      <div v-if="deniedAt" class="notification is-danger is-light">
        This trip was denied on {{ deniedAt | formatDateTime }}
      </div>

      <div v-if="needsReviewed && !allSegmentsCancelled" class="notification">
        Please review the travel request below then click either the "Approve"
        button or "Deny" button.
      </div>

      <div v-if="showPolicy && !allSegmentsCancelled" class="policy-details box">
        <h2 class="is-size-4">Travel Policy Details</h2>
        <h2 class="is-size-5 mb-4" v-if="travelPolicy">
          Policy Name / Rule Class: {{ travelPolicy.ruleClass }}
        </h2>

        <div class="my-2">
          <strong>Was policy applied to the booking?</strong>
          {{ policy.applied | translateString }}
        </div>
        <div class="my-2">
          <strong>Were policy rules triggered?</strong>
          {{ policy.triggered | translateString }}
        </div>

        <div class="my-5">
          <div v-if="policy.mostStrictApprovalRequired == 'HARD'">
            <b-tag class="is-danger is-light">
              <b-icon icon="alert-octagon" size="is-small"></b-icon>
              <span>Requires Manager Approval</span>
            </b-tag>
            If this trip is not approved it will be canceled.
          </div>

          <div v-if="policy.mostStrictApprovalRequired == 'SOFT'">
            <b-tag class="is-warning is-light">
              <b-icon icon="alert" size="is-small"></b-icon>
              <span>Triggers Manager Notification</span>
            </b-tag>
            If this trip is not denied it will be ticketed.
          </div>

          <div v-if="policy.mostStrictApprovalRequired == 'NONE'">
            <b-tag class="is-warning is-light">
              <b-icon icon="alert" size="is-small"></b-icon>
              <span>Trip Will Be Logged for Reporting</span>
            </b-tag>
            This trip does not need approved but will be logged for rule
            violations.
          </div>
        </div>

        <div class="columns">
          <div
            v-if="policy.triggered == 'Y' && rulesTriggered && travelPolicy"
            class="my-3 column"
          >
            <div class="">
              <h3 class="is-size-5 mb-3">
                Rules triggered
                <span class="ml-2 tag">{{ rulesTriggered.length }}</span>
              </h3>
              <div v-for="rule in rulesTriggered" :key="rule.id">
                <SegmentIcon
                  class="mr-2"
                  :segment="{ type: rule.category }"
                ></SegmentIcon>
                <span class="">{{ rule.triggeredMessage }}</span>
              </div>
            </div>
          </div>

          <div
            v-if="policy.triggered == 'Y' && violationNotes"
            class="my-3 column"
          >
            <div class="">
              <h3 class="is-size-5 mb-3">
                Message from Traveler (or Arranger)
                <span class="ml-2 tag">{{ violationNotes.length }}</span>
              </h3>
              <div
                v-for="violationNote in violationNotes"
                :key="violationNote.id"
              >
                <SegmentIcon
                  class="mr-2"
                  :segment="{ type: violationNote.category }"
                ></SegmentIcon>
                <span class="is-capitalized">{{ violationNote.message }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <TripDetail
        :recordLocatorHash="encryptedRecordLocator"
        :source="'APPROVALS_PAGE'"
      />

      <div v-if="needsReviewed && !allSegmentsCancelled" class="columns">
        <div class="column is-narrow">
          <b-button
            @click="confirmApprove()"
            type="is-success"
            icon-left="check-bold"
            >Approve</b-button
          >
        </div>
        <div class="column is-narrow">
          <b-button
            @click="confirmDeny()"
            type="is-danger"
            icon-left="close-circle"
            >Deny</b-button
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import TripCard from "../components/Trip/TripCard.vue";
import SegmentIcon from "../components/UI/SegmentIcon";
import TripDetail from "../views/TripDetail";

var moment = require("moment");

export default {
  name: "TravelRequestNotification",
  components: {
    SegmentIcon,
    TripDetail,
  },
  data: function() {
    return {
      isLoading: true,
      isFound: null,
      trip: {},
      travelRequestNotification: {},
      travelPolicy: null,
      recordLocator: null,
      key: null,
      showTripDetails: true,
      bookingId: null,
      isTravelChampionReview: false,
    };
  },
  computed: {
    encryptedRecordLocator() {
      return this.$helpers.encryptText(this.recordLocator);
    },
    policy() {
      return this.trip.policy;
    },
    showPolicy() {
      return this.policy;
    },
    rulesTriggered() {
      let rules = this.policy.rulesTriggered;
      let rulesData = [];
      let travelPolicy = this.travelPolicy;

      if (!travelPolicy) return rulesData;

      rules.forEach((rule) => {
        let matchingRules = travelPolicy.rules.filter((item) => {
          return item.id == rule;
        });

        if (matchingRules.length == 1) {
          rulesData.push(matchingRules[0]);
        }
      });

      return rulesData;
    },
    violationNotes() {
      let notesData = [];
      let violationNotes = this.policy.violationNotes;

      violationNotes.forEach((violationNote) => {
        let parts = violationNote.split("|");
        let type = parts[0].toLowerCase();
        let message = parts[1].toLowerCase();
        let noteItem = {
          id: Math.random(),
          category: type,
          message: message,
        };
        notesData.push(noteItem);
      });

      return notesData;
    },
    needsReviewed() {
      return (
        this.approvedAt == null &&
        this.deniedAt == null &&
        this.policy?.mostStrictApprovalRequired != 'NONE' && //Added a condition to not show buttons if no review required
        this.policy?.triggered === 'Y' //Added a condition to not show buttons if no policy triggered
      );
    },

    approvedAt() {
      var approvedAt = null;

      if (this.travelRequestNotification.approved_at != null) {
        approvedAt = this.travelRequestNotification.approved_at;
      } else if (this.trip.approvedAt != null) {
        approvedAt = this.trip.approvedAt;
      }

      return approvedAt;
    },
    deniedAt() {
      var deniedAt = null;

      if (this.travelRequestNotification.denied_at != null) {
        deniedAt = this.travelRequestNotification.denied_at;
      } else if (this.trip.deniedAt != null) {
        deniedAt = this.trip.deniedAt;
      }

      return deniedAt;
    },
    allSegmentsCancelled() {
      if (this.trip.segments && this.trip.segments.length > 0) {
        return this.trip.segments.every(segment => segment.status === 'cancelled');
      }
      return false;
    },
    requestParams() {
  const params = {
    recordLocator: this.recordLocator,
    key: this.key,
    officeId: this.trip.creationOfficeId
  };

  if (this.isTravelChampionReview && this.bookingId) {
    params.booking_id = this.bookingId;


  }

  return params;
},


    now() {
      return moment().format();
    },
  },
  methods: {
    async getTravelRequest() {
      if (!this.recordLocator || !this.key) {
        this.isFound = false;
        this.isLoading = false;

        this.$buefy.toast.open({
          message:
            "The page is missing a record locator or key. Please check the URL you are trying to access.",
          type: "is-danger",
        });

        return;
      }

      this.$app
        .huddleLabsServerCall("approvals/show", this.requestParams)
        .then((result) => {
          if (result.data) {
            this.trip = result.data;
            this.travelRequestNotification =
              result.included.travelRequestNotification;
            this.isFound = true;
            this.isLoading = false;
            this.travelPolicy = result.included.travelPolicy;
            if (this.action == "approve") {
              this.confirmApprove();
            } else if (this.action == "deny") {
              this.confirmDeny();
            }
          } else {
            this.trip = null;
            this.isFound = false;
            this.isLoading = false;
          }
        });
    },
    setParams() {
      this.recordLocator = this.$route.query.record_locator;
      this.key = this.$route.query.key;
      this.action = this.$route.query.action;
      this.bookingId = this.$route.query.booking_id;

      // Detect Travel Champion review mode by checking the route path

      this.isTravelChampionReview = this.$route.path.startsWith("/admin/trip-library/approvals");

    },
    confirmApprove() {
      if (!this.needsReviewed) return;

      this.$buefy.dialog.confirm({
        title: "Approve Travel Request",
        message:
          "Are you sure you want to approve this trip? This action cannot be undone.",
        confirmText: "Approve request",
        type: "is-success",
        /*inputAttrs: {
                        type: 'textarea',
                        placeholder: 'Optional message to traveler...',
                        value: null,
                        required: false
                    },*/
        hasIcon: true,
        onConfirm: () => this.approve(),
      });
    },
    confirmDeny() {
      if (!this.needsReviewed) return;

      this.$buefy.dialog.confirm({
        title: "Deny Travel Request",
        message:
          "Are you sure you want to deny this trip? This action cannot be undone.",
        confirmText: "Deny request",
        type: "is-danger",
        hasIcon: true,
        onConfirm: () => this.deny(),
      });
    },
    approve() {
      this.$app
        .huddleLabsServerCall("approvals/approve", this.requestParams)
        .then((result) => {
          // console.log('approved')

          this.travelRequestNotification.approved_at = this.now;

          this.$buefy.toast.open({
            message: "You have approved this trip!",
            type: "is-success",
          });
        });
    },
    deny() {
      this.$app
        .huddleLabsServerCall("approvals/deny", this.requestParams)
        .then((result) => {
          // console.log('denied')

          this.travelRequestNotification.denied_at = this.now;

          this.$buefy.toast.open({
            message: "You have denied this trip!",
            type: "is-danger",
          });
        });
    },
  },
  mounted() {
    this.setParams();
    this.getTravelRequest();
  },
};
</script>

<style scoped></style>
